// Экран 11 · Профиль и данные. SMY-75, имя, фото и пароль — SMY-121
//
// Право на прозрачность и контроль данных. Здесь нет ни одного тогла, который
// запоминал бы выбор, а продукт бы его не исполнял (SMY-110), — и по этому же
// правилу из раздела ушли двое.
//
// «Обработка в России» была не настройкой, а состоянием продукта: тогл стоял
// включённым и заблокированным, потому что второго положения у него не
// существует. Заблокированный тогл всё равно читается как настройка — человек
// ищет, чем его переключить. Утверждение осталось утверждением и живёт в
// политике, а не строкой с переключателем.
//
// «Память проектов» ушла следом за проектами (06.09.2026): папки вокруг цели
// в продукте больше нет, и настройка обещала влиять на то, чего человек
// нигде не видит. Сервер про неё ещё знает и держит своё умолчание — вернутся
// проекты, вернётся и строка.
//
// Удаление необратимо, и подтверждение у него настоящее: слово печатается
// руками, а список того, что исчезнет и что останется, приходит с сервера — он
// же и есть политика.
//
// Имя, фотография и пароль живут здесь, а не только в модалках после входа.
// Модалка спрашивает один раз и закрывается навсегда; без этого экрана
// закрывший её не завёл бы пароль уже никогда, а передумавший — не снял бы.

import { useEffect, useRef, useState, type FormEvent } from 'react'
import {
  checkEmail,
  checkPassword,
  EMAIL_PROBLEM_TEXT,
  PASSWORD_MIN_LENGTH,
  PASSWORD_OFFER_TEXT,
  PASSWORD_PROBLEM_TEXT,
} from '@smyslika/core'
import { ApiError, type DeletionTerms, type Settings } from '@smyslika/api-client'

import { COMPLAINT_MAX_CHARS, type ComplaintKind } from '@smyslika/core'
import type { Complaint } from '@smyslika/api-client'

import { sessionToken, useSession } from '../session.tsx'
import { Shell } from '../shell/Shell.tsx'
import { Avatar, Button, Caps, Chip, Field, Icon, Sheet, Toggle } from '../ui/index.ts'

/** Строка группы: название, пояснение и что-нибудь справа. */
function Line({
  title,
  hint,
  right,
  onClick,
  danger,
  dim,
}: {
  title: string
  hint?: string
  right?: React.ReactNode
  onClick?: () => void
  danger?: boolean
  /** Настройка, которую пока нельзя переключить: показана в истинном виде. */
  dim?: boolean
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      className={`line${danger === true ? ' line--danger' : ''}${dim === true ? ' line--dim' : ''}`}
      {...(onClick ? { type: 'button' as const, onClick } : {})}
    >
      <span className="line__body">
        <span className="line__title">{title}</span>
        {hint != null ? <span className="line__hint">{hint}</span> : null}
      </span>
      {right}
    </Tag>
  )
}

export function Profile() {
  const { account, complaints: api, me, refreshMe, reference, signOut, tariff } = useSession()

  const [settings, setSettings] = useState<Settings | null>(null)
  const [terms, setTerms] = useState<DeletionTerms | null>(null)
  const [rules, setRules] = useState(false)
  /**
   * Обращения. SMY-80
   *
   * Открытые сверху и с датой, к которой обещали ответить: обращение без
   * срока читается как «отправил в пустоту», и второе такое человек уже не
   * напишет — он напишет в сторы.
   */
  const [complaining, setComplaining] = useState(false)
  const [complaintText, setComplaintText] = useState('')
  const [complaintKind, setComplaintKind] = useState<ComplaintKind>('content')
  const [complaints, setComplaints] = useState<Complaint[] | null>(null)
  const [promise, setPromise] = useState<string | null>(null)
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  /** Имя правится прямо в карточке: заводить ради одного поля окно незачем. */
  const [editingName, setEditingName] = useState(false)
  const [name, setName] = useState('')
  const photoInput = useRef<HTMLInputElement>(null)

  /**
   * Окно пароля. Одно на все три действия — завести, сменить, снять: у них
   * общие поля и общий довод «текущий пароль обязателен, если он уже есть».
   * `null` — окно закрыто.
   */
  const [pwSheet, setPwSheet] = useState<'set' | 'remove' | null>(null)

  /**
   * Окно почты. Окном, а не правкой в строке, как у имени: у почты есть
   * проверка, а значит и подпись под полем, которой строке негде поместиться.
   */
  const [emailSheet, setEmailSheet] = useState(false)

  async function saveName(): Promise<void> {
    setBusy(true)
    setNote(null)
    try {
      await account.setName(name)
      await refreshMe()
      setEditingName(false)
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Имя не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  async function savePhoto(file: File): Promise<void> {
    setBusy(true)
    setNote(null)
    try {
      await account.setAvatar(file, file.name)
      await refreshMe()
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Фотография не загрузилась')
    } finally {
      setBusy(false)
      // Поле чистится всегда: иначе повторный выбор ТОГО ЖЕ файла не вызовет
      // события change, и человек решит, что кнопка сломалась.
      if (photoInput.current != null) photoInput.current.value = ''
    }
  }

  async function dropPhoto(): Promise<void> {
    setBusy(true)
    setNote(null)
    try {
      await account.removeAvatar()
      await refreshMe()
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Фотография не удалилась')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    let alive = true
    void account.settings().then((s) => alive && setSettings(s)).catch(() => {})
    return () => {
      alive = false
    }
  }, [account])

  /** Переключение уходит на сервер сразу: настройка привязана к человеку. */
  function toggle(patch: Partial<Settings>): void {
    if (settings == null) return
    const before = settings
    setSettings({ ...settings, ...patch })
    setNote(null)
    account.setSetting(patch).then(setSettings, (e: Error) => {
      // Возвращаем как было и говорим почему: тогл, откатившийся молча,
      // выглядит сломанным.
      setSettings(before)
      setNote(e.message)
    })
  }

  const tariffTitle = reference?.tariffs.find((t) => t.id === tariff)?.label

  return (
    <Shell account>
      <div className="content account">
        <h1 className="account__title">Профиль и данные</h1>

        <div className="profile__card">
          {/* Фотография меняется нажатием на неё саму, а не кнопкой рядом:
              кружок и есть та кнопка, на которую человек целится. Поле файла
              спрятано — его вид браузер не даёт настроить, и в макете ему
              места нет. */}
          <button
            type="button"
            className="profile__photo"
            disabled={busy}
            onClick={() => photoInput.current?.click()}
            aria-label={me?.avatarUrl != null ? 'Заменить фотографию' : 'Загрузить фотографию'}
          >
            <Avatar
              size={46}
              {...(me?.name != null ? { name: me.name } : {})}
              {...(me?.avatarUrl != null ? { src: me.avatarUrl } : {})}
            />
            {/* Плюс, а не фотоаппарат: своего значка камеры в наборе нет, а
                дорисовывать его ради одной кнопки значит завести иконку, за
                которой не стоит ни одного другого экрана. */}
            <span className="profile__photo-hint">
              <Icon name="plus" size={13} strokeWidth={2.4} />
            </span>
          </button>
          <input
            ref={photoInput}
            type="file"
            accept="image/jpeg,image/png"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file != null) void savePhoto(file)
            }}
          />

          <span className="profile__who">
            {editingName ? (
              // Форма, а не поле с обработчиком клавиш: Enter в форме
              // отправляет её сам, без нашего участия. Обработчик здесь уже
              // стоял и молчал — событие до него доходило (Escape работал),
              // а сохранение не случалось. Форма снимает вопрос целиком:
              // это то, для чего она есть.
              <form
                className="profile__name-edit"
                onSubmit={(e) => {
                  e.preventDefault()
                  void saveName()
                }}
              >
                <Field
                  boxy
                  autoFocus
                  value={name}
                  aria-label="Имя"
                  placeholder="Как к тебе обращаться"
                  onChange={(e) => setName(e.target.value)}
                  // Escape — отмена. У неё формы нет, и обработчик остаётся.
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditingName(false)
                  }}
                />
                <Button type="submit" kind="ghost" className="btn--small" disabled={busy}>
                  Сохранить
                </Button>
              </form>
            ) : (
              <button
                type="button"
                className="profile__name profile__name--button"
                onClick={() => {
                  setName(me?.name ?? '')
                  setEditingName(true)
                }}
              >
                {me?.name ?? 'Добавить имя'}
                <Icon name="write" size={15} strokeWidth={2} />
              </button>
            )}
            <span className="profile__meta">
              {[me?.email ?? null, tariffTitle != null ? `тариф ${tariffTitle}` : null]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn btn--ghost" onClick={() => void signOut()}>
            Выйти
          </button>
        </div>

        {/* Убрать фотографию — отдельной строкой и только когда она есть:
            постоянная кнопка «убрать» рядом с пустым кружком предлагает
            удалить то, чего нет. */}
        {me?.avatarUrl != null ? (
          <button type="button" className="signin__link profile__drop-photo" disabled={busy} onClick={() => void dropPhoto()}>
            Убрать фотографию
          </button>
        ) : null}

        <Caps>Вход</Caps>
        <div className="group">
          {/* Почта — не настройка, а то, чем человек входит. До 10.09.2026
              строка стояла БЕЗ ДЕЙСТВИЯ, и оговорка рядом обещала, что смена
              логина будет отдельной операцией с подтверждением. Операция
              появилась (SMY-209): код на новый адрес, письмо на старый,
              гашение остальных сеансов. Обещание сдержано, строка ожила. */}
          <Line
            title="Почта"
            hint={
              me?.email != null
                ? `${me.email} — логин, способ восстановить доступ и адрес для чека`
                : 'Логин и способ восстановить доступ'
            }
            onClick={() => setEmailSheet(true)}
            right={
              <span className="row__chevron">
                <Icon name="chevron" size={18} />
              </span>
            }
          />
          <Line
            title="Пароль"
            hint={
              me?.hasPassword === true
                ? 'Задан. Смена закроет все остальные устройства'
                : `Чтобы входить без ожидания письма. Не короче ${PASSWORD_MIN_LENGTH} знаков`
            }
            onClick={() => setPwSheet('set')}
            right={
              <span className="row__chevron">
                <Icon name="chevron" size={18} />
              </span>
            }
          />
          {me?.hasPassword === true ? (
            <Line
              title="Убрать пароль"
              hint="Вход по коду из письма останется — аккаунт не запрётся"
              onClick={() => setPwSheet('remove')}
              right={
                <span className="row__chevron">
                  <Icon name="chevron" size={18} />
                </span>
              }
            />
          ) : null}
        </div>

        {/* ⚠️ РАЗДЕЛ «ЧЕКИ» УБРАН 10.09.2026 (SMY-209). Он показывал ту же
            самую почту второй строкой: колонка одна с SMY-151, а с переходом
            на вход по почте (SMY-175) «адрес для чека» и «логин» окончательно
            перестали быть разными вещами.

            Две строки про один адрес — это не забота, а вопрос «какую из них
            менять»: поменяв одну, человек вправе ждать, что вторая осталась
            прежней. Адрес назван в подписи строки «Почта» выше, вместе с тем,
            что чек приходит на него же. */}


        <Caps>Данные</Caps>
        <div className="group">
          {/* Цена выключения названа прямо. Маскирование не бесплатно:
              модель не может просклонять то, чего не видит, и в претензии
              выходит «От Иванов Иван Иванович» — ради этого тогл и оставлен
              выключаемым (docs/data-flow.md). Человек, который выключает его,
              должен знать, что покупает и чем платит. */}
          <Line
            title="Маскировать персональные данные"
            hint="Скрывать имена, адреса и номера перед отправкой. Выключишь — модель увидит их и сможет склонять в документах"
            right={
              <Toggle
                checked={settings?.maskPii ?? true}
                disabled={settings == null}
                label="Маскировать персональные данные"
                onChange={(v) => toggle({ maskPii: v })}
              />
            }
          />
        </div>

        {/* Документы — ссылками, а не пересказом. SMY-215
            Фраза согласия на входе называет оферту и политику; до 10.09.2026
            обе были словами без адреса. Здесь они впервые становятся тем, что
            можно открыть, — а политика по ст. 18.1 152-ФЗ обязана быть
            доступна и вовсе неограниченно. */}
        <Caps>Документы</Caps>
        <div className="group">
          <Line
            title="Оферта"
            hint="Условия, на которых мы оказываем услуги"
            onClick={() => window.open('/legal/offer', '_blank', 'noopener')}
            right={
              <span className="row__chevron">
                <Icon name="chevron" size={18} />
              </span>
            }
          />
          <Line
            title="Политика обработки данных"
            hint="Что мы собираем, кому передаём и сколько храним"
            onClick={() => window.open('/legal/privacy', '_blank', 'noopener')}
            right={
              <span className="row__chevron">
                <Icon name="chevron" size={18} />
              </span>
            }
          />
          {/* Правила использования — третья строка, SMY-13. Отдельным
              документом, а не абзацем в оферте: человек открывает их, когда
              получил отказ, и искать причину в договоре он не пойдёт. */}
          <Line
            title="Правила использования"
            hint="Что нельзя просить и загружать, что будет при нарушении"
            onClick={() => window.open('/legal/aup', '_blank', 'noopener')}
            right={
              <span className="row__chevron">
                <Icon name="chevron" size={18} />
              </span>
            }
          />
          {/* Правила списания — четвёртая, SMY-11. Здесь для полноты списка
              документов; читают их с экрана лимитов, и ссылка стоит там же. */}
          <Line
            title="Правила списания единиц"
            hint="За что списывается единица и что не списывается никогда"
            onClick={() => window.open('/legal/units', '_blank', 'noopener')}
            right={
              <span className="row__chevron">
                <Icon name="chevron" size={18} />
              </span>
            }
          />
        </div>

        <Caps>Прозрачность</Caps>
        <div className="group">
          {/* Выключено по умолчанию — правило тона: не называть модели, пока
              не спросили. Тогл и есть тот вопрос, и держится он на сервере:
              имя модели просто не приезжает, пока он выключен. */}
          <Line
            title="Показывать, кто выполнил задачу"
            hint="Модель — в деталях результата, за кнопкой «ещё»"
            right={
              <Toggle
                checked={settings?.showModel ?? false}
                disabled={settings == null}
                label="Показывать, кто выполнил задачу"
                onChange={(v) => toggle({ showModel: v })}
              />
            }
          />
          <Line
            title="Правила списания лимита"
            onClick={() => setRules(true)}
            right={
              <span className="row__chevron">
                <Icon name="chevron" size={18} />
              </span>
            }
          />
        </div>

        <div className="group">
          <Line
            title="Скачать мои данные"
            hint="Всё, что о тебе хранится, одним файлом"
            onClick={() => {
              // Выгрузку отдаёт закрытая токеном ручка — обычной ссылкой её не
              // откроешь, заголовок авторизации в ссылку не положишь.
              setBusy(true)
              setNote(null)
              fetch('/v1/account/export', {
                headers: { authorization: `Bearer ${sessionToken() ?? ''}` },
              })
                .then(async (res) => {
                  // Отказ сервер называет словами и со сроком — в том числе
                  // очередь выгрузки, 429 (SMY-231). Тело может не быть JSON,
                  // если ответил не api, а прокси.
                  if (!res.ok) {
                    const body = (await res.json().catch(() => null)) as { error?: unknown } | null
                    throw new Error(typeof body?.error === 'string' ? body.error : 'Выгрузка не удалась')
                  }
                  // Выгрузка идёт потоком, и оборваться может посреди: такой
                  // файл не сохраняется вовсе, а называется ошибкой.
                  const blob = await res.blob().catch(() => {
                    throw new Error('Выгрузка не скачалась целиком. Проверь связь и попробуй ещё раз')
                  })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `smyslika-${new Date().toISOString().slice(0, 10)}.json`
                  a.click()
                  URL.revokeObjectURL(url)
                })
                .catch((e: Error) => setNote(e.message))
                .finally(() => setBusy(false))
            }}
            right={
              <span className="row__chevron">
                <Icon name="chevron" size={18} />
              </span>
            }
          />
          <Line
            title="Пожаловаться или оспорить"
            hint={
              complaints == null || complaints.length === 0
                ? 'Ответим в срок и напишем, что решили'
                : `${complaints.filter((c) => c.status === 'new').length} на рассмотрении из ${complaints.length}`
            }
            onClick={() => {
              setComplaintText('')
              setPromise(null)
              setNote(null)
              setComplaining(true)
              void api.list().then(setComplaints).catch(() => setComplaints([]))
            }}
            right={
              <span className="row__chevron">
                <Icon name="chevron" size={18} />
              </span>
            }
          />
          <Line
            title="Удалить аккаунт и историю"
            danger
            onClick={() => {
              setConfirm('')
              setNote(null)
              void account.deletionTerms().then(setTerms).catch((e: Error) => setNote(e.message))
            }}
          />
        </div>

        {note != null ? (
          <p className="account__note" role="status">
            {note}
          </p>
        ) : null}
      </div>

      <Sheet open={rules} onClose={() => setRules(false)} label="Правила списания лимита" closable>
        <h2 className="confirm__title">Правила списания лимита</h2>
        <p className="confirm__explain">
          Единица лимита снимается за завершённый результат — не за шаг, не за вызов модели и не за уточнение.
        </p>
        <ul className="rules__list">
          {(reference?.notCharged ?? []).map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        <div className="confirm__actions">
          <button type="button" className="btn btn--primary" onClick={() => setRules(false)}>
            Понятно
          </button>
        </div>
      </Sheet>

      <Sheet open={complaining} onClose={() => setComplaining(false)} label="Обращение" closable>
        <h2 className="confirm__title">Пожаловаться или оспорить</h2>
        <p className="confirm__explain">
          Напиши, что случилось. Разберёмся и ответим здесь же.
        </p>

        {/* Вид обращения выбирает человек, а не мы за него: у оспаривания
            другой срок ответа, и подменить его молча значит подменить
            обещание. */}
        <div className="chips">
          <Chip selected={complaintKind === 'content'} onClick={() => setComplaintKind('content')}>
            Жалоба на результат
          </Chip>
          <Chip selected={complaintKind === 'appeal'} onClick={() => setComplaintKind('appeal')}>
            Оспорить остановку задач
          </Chip>
        </div>

        {/* Многострочное поле, а не пилюля чата: обращение пишут абзацем, и
            строка в одну высоту заставляла бы писать телеграммой. */}
        <textarea
          className="field field--boxy complaint__text"
          rows={5}
          maxLength={COMPLAINT_MAX_CHARS}
          aria-label="Что случилось"
          placeholder={
            complaintKind === 'appeal'
              ? 'Расскажи, как ты пользовался продуктом'
              : 'Опиши своими словами'
          }
          value={complaintText}
          onChange={(e) => setComplaintText(e.target.value)}
        />

        <div className="confirm__actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || complaintText.trim() === ''}
            onClick={() => {
              setBusy(true)
              setNote(null)
              api
                .file({ kind: complaintKind, text: complaintText })
                .then((filed) => {
                  // Обещание показываем словами сервера: срок живёт в одном
                  // месте, и клиент его не пересчитывает.
                  setPromise(filed.promise)
                  setComplaintText('')
                  return api.list().then(setComplaints)
                })
                .catch((e: Error) => setNote(e.message))
                .finally(() => setBusy(false))
            }}
          >
            Отправить
          </button>
        </div>

        {promise != null ? (
          <p className="account__note" role="status">
            {promise}
          </p>
        ) : null}

        {complaints != null && complaints.length > 0 ? (
          <>
            <Caps>Мои обращения</Caps>
            <ul className="rules__list">
              {complaints.map((c) => (
                <li key={c.id}>
                  {c.status === 'new'
                    ? `На рассмотрении, ответим до ${new Date(c.dueAt).toLocaleDateString('ru-RU')}`
                    : (c.resolution ?? (c.status === 'resolved' ? 'Решено' : 'Отклонено'))}
                  {' — '}
                  {c.body.slice(0, 80)}
                  {c.body.length > 80 ? '…' : ''}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </Sheet>

      <Sheet
        open={terms != null}
        onClose={() => setTerms(null)}
        label="Удаление аккаунта"
        closable
      >
        <h2 className="confirm__title">Удалить аккаунт и историю</h2>
        <p className="confirm__explain">{terms?.timing}</p>

        <Caps>Исчезнет</Caps>
        <ul className="rules__list">
          {(terms?.removed ?? []).map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>

        {(terms?.kept ?? []).map((k) => (
          <p key={k.what} className="account__fine">
            <b>{k.what}</b> — {k.why}
          </p>
        ))}

        <p className="confirm__explain">
          Чтобы подтвердить, напечатай слово <b>{terms?.confirmationWord}</b>.
        </p>
        <Field
          boxy
          value={confirm}
          placeholder={terms?.confirmationWord ?? ''}
          onChange={(e) => setConfirm(e.target.value)}
        />

        <div className="confirm__actions">
          <button type="button" className="btn btn--ghost" onClick={() => setTerms(null)}>
            Оставить всё как есть
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy || confirm !== terms?.confirmationWord}
            onClick={() => {
              setBusy(true)
              account
                .remove(confirm)
                // Выход сам уведёт на экран входа: сессии больше нет.
                .then(() => void signOut())
                .catch((e: Error) => setNote(e.message))
                .finally(() => setBusy(false))
            }}
          >
            Удалить навсегда
          </button>
        </div>
      </Sheet>

      {emailSheet ? (
        <EmailSheet
          {...(me?.email != null ? { email: me.email } : {})}
          onClose={() => setEmailSheet(false)}
          onDone={() => {
            setEmailSheet(false)
            void refreshMe()
          }}
        />
      ) : null}

      {pwSheet != null ? (
        <PasswordSheet
          mode={pwSheet}
          hasPassword={me?.hasPassword === true}
          {...(me?.email != null ? { login: me.email } : {})}
          onClose={() => setPwSheet(null)}
          onDone={() => {
            setPwSheet(null)
            void refreshMe()
          }}
        />
      ) : null}
    </Shell>
  )
}

/**
 * Почта для чека: завести или сменить.
 *
 * Убрать нельзя, и кнопки «убрать» здесь нет намеренно. Чек мы обязаны выдать
 * по каждой оплате (ст. 14 422-ФЗ), и пустой адрес означал бы не «забудьте
 * меня», а «выдавайте чек в никуда». Кто хочет, чтобы мы забыли, удаляет
 * аккаунт — там почта исчезает вместе со всем остальным.
 */
/**
 * Смена почты. SMY-209
 *
 * ⚠️ ДВА ШАГА, И ЭТО НЕ ЦЕРЕМОНИЯ. Раньше здесь была одна форма, писавшая
 * адрес напрямую: почта считалась «адресом для чека», и цена ошибки была
 * невелика. С переходом на вход по почте та же колонка стала ЛОГИНОМ — и
 * форма без подтверждения превратилась бы в способ сменить себе способ входа
 * молча, из чужой открытой сессии.
 *
 * Поэтому: код на НОВЫЙ адрес (значит, человек до него дошёл), уведомление на
 * старый (значит, владелец узнает) и гашение остальных сеансов — всё это
 * делает сервер, а окно только ведёт человека по двум шагам.
 */
function EmailSheet({
  email: current,
  onClose,
  onDone,
}: {
  email?: string
  onClose: () => void
  onDone: () => void
}) {
  const { account } = useSession()
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Правило из ядра — то же, которым проверит сервер. Пустое поле молчит:
  // подпись об ошибке под нетронутым полем читается как упрёк.
  const problem = email.trim() === '' ? null : checkEmail(email)

  async function ask(e: FormEvent): Promise<void> {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await account.requestEmailChange(email)
      setStep('code')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Код не отправился')
    } finally {
      setBusy(false)
    }
  }

  async function confirm(e: FormEvent): Promise<void> {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await account.confirmEmailChange(email, code)
      onDone()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Адрес не сменился')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open onClose={onClose} closable label="Смена почты">
      {step === 'email' ? (
        <form onSubmit={(e) => void ask(e)}>
          <div className="onboard__head">
            <h2 className="onboard__title">Сменить почту</h2>
            <p className="onboard__explain">
              Почта — это логин и способ восстановить доступ. Отправим код на новый адрес, а на{' '}
              {current ?? 'старый'} — письмо о смене. Остальные сеансы завершатся.
            </p>
          </div>

          <label className="onboard__label" htmlFor="profile-email">
            Новый адрес
          </label>
          <Field
            id="profile-email"
            boxy
            autoFocus
            type="email"
            inputMode="email"
            value={email}
            autoComplete="email"
            placeholder="Например, kirill@example.ru"
            onChange={(e) => setEmail(e.target.value)}
          />

          {problem != null ? <p className="onboard__hint">{EMAIL_PROBLEM_TEXT[problem]}</p> : null}

          {error != null ? (
            <p className="onboard__error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="onboard__actions">
            <Button kind="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" disabled={busy || email.trim() === '' || problem != null}>
              Отправить код
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={(e) => void confirm(e)}>
          <div className="onboard__head">
            <h2 className="onboard__title">Код с нового адреса</h2>
            <p className="onboard__explain">Отправили на {email}. Введите код из письма.</p>
          </div>

          <label className="onboard__label" htmlFor="profile-email-code">
            Код
          </label>
          <Field
            id="profile-email-code"
            boxy
            autoFocus
            inputMode="numeric"
            value={code}
            autoComplete="one-time-code"
            onChange={(e) => setCode(e.target.value)}
          />

          {error != null ? (
            <p className="onboard__error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="onboard__actions">
            {/* Назад, а не «Отмена»: человек уже получил письмо, и вернуться
                он хочет к полю адреса — исправить опечатку, а не выйти. */}
            <Button kind="ghost" onClick={() => setStep('email')}>
              Назад
            </Button>
            <Button type="submit" disabled={busy || code.trim() === ''}>
              Сменить
            </Button>
          </div>
        </form>
      )}
    </Sheet>
  )
}

/**
 * Пароль: завести, сменить, снять. Одно окно на три действия.
 *
 * Отдельным компонентом, а не куском разметки в `Profile`, ради состояния:
 * поля пароля обязаны исчезать вместе с окном. Оставшись в состоянии экрана,
 * они пережили бы закрытие — то есть набранный и не сохранённый пароль
 * дождался бы следующего открытия и молча ушёл бы на сервер по нажатию
 * «Сохранить».
 */
function PasswordSheet({
  mode,
  hasPassword,
  login,
  onClose,
  onDone,
}: {
  mode: 'set' | 'remove'
  hasPassword: boolean
  login?: string
  onClose: () => void
  onDone: () => void
}) {
  const { account } = useSession()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [shown, setShown] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const removing = mode === 'remove'
  // Правила из ядра — те же, что проверит сервер.
  // Логин — почта (SMY-167): правило сравнивает имя до собаки.
  const problem = next === '' ? null : checkPassword(next, login)
  const ready = removing ? current !== '' : next !== '' && problem == null && (!hasPassword || current !== '')

  async function submit(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      if (removing) await account.removePassword(current)
      else await account.setPassword(next, hasPassword ? current : undefined)
      onDone()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open onClose={onClose} closable label={removing ? 'Убрать пароль' : 'Пароль'}>
      <div className="onboard__head">
        <h2 className="onboard__title">
          {removing ? 'Убрать пароль' : hasPassword ? 'Сменить пароль' : 'Завести пароль'}
        </h2>
        <p className="onboard__explain">
          {removing
            ? 'Входить останется по коду из письма — тем же способом, которым подтверждён аккаунт. Ничего, кроме второго способа входа, не теряется.'
            : hasPassword
              ? 'После смены все остальные устройства выйдут из аккаунта. Это и есть смысл смены: пароль меняют, когда подозревают, что им пользуется кто-то ещё.'
              : PASSWORD_OFFER_TEXT}
        </p>
      </div>

      {hasPassword ? (
        <>
          <label className="onboard__label" htmlFor="pw-current">
            Текущий пароль
          </label>
          <Field
            id="pw-current"
            boxy
            autoFocus
            value={current}
            type="password"
            autoComplete="current-password"
            onChange={(e) => setCurrent(e.target.value)}
          />
        </>
      ) : null}

      {removing ? null : (
        <>
          <label className="onboard__label" htmlFor="pw-next">
            {hasPassword ? 'Новый пароль' : 'Пароль'}
          </label>
          <div className="onboard__secret">
            <Field
              id="pw-next"
              boxy
              autoFocus={!hasPassword}
              value={next}
              type={shown ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={`Не короче ${PASSWORD_MIN_LENGTH} знаков`}
              onChange={(e) => setNext(e.target.value)}
            />
            <button
              type="button"
              className="onboard__peek"
              onClick={() => setShown((v) => !v)}
              aria-label={shown ? 'Скрыть пароль' : 'Показать пароль'}
            >
              {shown ? 'Скрыть' : 'Показать'}
            </button>
          </div>
          {problem != null ? <p className="onboard__hint">{PASSWORD_PROBLEM_TEXT[problem]}</p> : null}
        </>
      )}

      {error != null ? (
        <p className="onboard__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="confirm__actions">
        <Button kind="ghost" onClick={onClose}>
          Отмена
        </Button>
        <Button
          kind={removing ? 'danger' : 'primary'}
          disabled={busy || !ready}
          onClick={() => void submit()}
        >
          {removing ? 'Убрать пароль' : 'Сохранить'}
        </Button>
      </div>
    </Sheet>
  )
}
